import * as React from 'react';
import ClaimsApi from '../api/ClaimsApi';
import { handleUnauthorizedResponse } from '../api/http';
import { FORM_NAME } from '../components/fastForm/data/constants';
import {
	FormBuilder,
	FormShape,
	SaveFastForm,
} from '../components/fastForm/types';
import { claimUpdateBuilder } from '../components/fastForm/updateBuilders';
import { errorToast } from '../components/Toast';
import {
	ClaimResponse,
	ClaimUpdateRequest,
	SubClaimPayableCreateRequest,
	SubClaimPayableResponse,
	SubClaimPayableReverseRequest,
	SubClaimSummary,
} from '../types/claim';
import { AuthContext, AuthData } from './auth';
import { PolicyContext, PolicyData } from './policy';

export const ClaimContext = React.createContext<ClaimData>(null);
export const useClaimContext = () => React.useContext(ClaimContext);

export interface ClaimData {
	claim: ClaimResponse;
	subclaimSummaries: SubClaimSummary[];
	claimUpdateReq: ClaimUpdateRequest;
	editMode: boolean;
	fields: FormShape<FormBuilder>;
	loadData: (claimId: string) => Promise<ClaimData>;
	updateClaim: (
		claimId: string,
		payload: ClaimUpdateRequest,
	) => Promise<ClaimData>;
	discardClaim: (claimId: string) => Promise<void>;
	fetchSubclaimSummaries: (claim: ClaimResponse) => Promise<ClaimData>;
	fetchSubclaimPayable: (
		payableLocator: string,
	) => Promise<SubClaimPayableResponse>;
	createSubclaimPayable: (
		claimId: string,
		payload: SubClaimPayableCreateRequest,
	) => Promise<ClaimData>;
	reverseSubclaimPayable: (
		claimId: string,
		payload: SubClaimPayableReverseRequest,
	) => Promise<SubClaimPayableResponse>;
	setFields: (fields: FormShape<FormBuilder>) => Promise<ClaimData>;
	saveFastForm: SaveFastForm<ClaimData>;
	setClaim: (claim: ClaimResponse) => Promise<ClaimData>;
	setSubclaimSummaries: (
		subclaimSummaries: SubClaimSummary[],
	) => Promise<ClaimData>;
	setClaimUpdateReq: (
		premiumReportUpdateReq: ClaimUpdateRequest,
	) => Promise<ClaimData>;
	setEditMode: (editMode: boolean) => Promise<ClaimData>;
	setLoading?: (loading: boolean) => void;
	clear: () => Promise<ClaimData>;
}

interface Props {
	authData: AuthData;
	policyData: PolicyData;
}

export class ClaimProvider extends React.Component<Props, ClaimData> {
	constructor(props: any, context: any) {
		super(props);
		this.state = {
			claim: null,
			subclaimSummaries: [],
			claimUpdateReq: {},
			editMode: false,
			fields: null,
			loadData: this.loadData,
			updateClaim: this.updateClaim,
			discardClaim: this.discardClaim,
			fetchSubclaimSummaries: this.fetchSubclaimSummaries,
			fetchSubclaimPayable: this.fetchSubclaimPayable,
			createSubclaimPayable: this.createSubclaimPayable,
			reverseSubclaimPayable: this.reverseSubclaimPayable,
			setFields: this.setFields,
			saveFastForm: this.saveFastForm,
			setClaim: this.setClaim,
			setSubclaimSummaries: this.setSubclaimSummaries,
			setClaimUpdateReq: this.setClaimUpdateReq,
			setEditMode: this.setEditMode,
			clear: this.clear,
		};
	}

	public render() {
		return (
			<ClaimContext.Provider value={this.state}>
				{this.props.children}
			</ClaimContext.Provider>
		);
	}

	private setClaim = (claim: ClaimResponse) => {
		const promise = new Promise<ClaimData>((resolve, reject) => {
			this.setState({ claim }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setSubclaimSummaries = (subclaimSummaries: SubClaimSummary[]) => {
		const promise = new Promise<ClaimData>((resolve, reject) => {
			this.setState({ subclaimSummaries }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setClaimUpdateReq = (claimUpdateReq: ClaimUpdateRequest) => {
		const promise = new Promise<ClaimData>((resolve, reject) => {
			this.setState({ claimUpdateReq }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setEditMode = (editMode: boolean) => {
		const promise = new Promise<ClaimData>((resolve, reject) => {
			this.setState({ editMode }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private fetchSubclaimSummaries = async (claim: ClaimResponse) => {
		const { authData } = this.props;
		// get all subclaim summaries
		const subclaims = claim.subClaims;
		if (subclaims?.length) {
			const subclaimSummaryPromises = await Promise.all(
				subclaims.map((subclaim) => {
					return ClaimsApi.fetchSubclaimSummary(
						authData.auth.authorizationToken,
						subclaim.locator,
					);
				}),
			);
			const subclaimSummaries = subclaimSummaryPromises.map((ssp) => ssp.data);
			return this.setSubclaimSummaries(subclaimSummaries);
		}
		return this.setSubclaimSummaries([]);
	};

	private fetchSubclaimPayable = async (payableLocator: string) => {
		const { authData } = this.props;
		const subClaimPayable = await ClaimsApi.getPayable(
			authData.auth.authorizationToken,
			payableLocator,
		);
		return subClaimPayable.data;
	};

	private loadData = async (claimId: string) => {
		const { authData } = this.props;
		if (!authData) {
			return Promise.reject();
		}
		if (!authData.auth) {
			return Promise.reject();
		}
		try {
			// get claim object
			const claimPromise = await ClaimsApi.getClaim(
				authData.auth.authorizationToken,
				claimId,
			);
			const claim = claimPromise.data;
			// get all subclaim summaries
			this.fetchSubclaimSummaries(claim);
			await this.setClaim(claim);
			return this.setEditMode(claim.currentStatus === 'pending');
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return Promise.reject();
			}
			errorToast('Failed to fetch necessary data');
			return Promise.reject();
		}
	};

	private setFields = (fields: FormShape<FormBuilder>) => {
		const promise = new Promise<ClaimData>((resolve, reject) => {
			this.setState({ fields }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private clear = () => {
		const promise = new Promise<ClaimData>((resolve, reject) => {
			this.setState(
				{
					claim: null,
					claimUpdateReq: {},
					editMode: false,
				},
				() => {
					resolve(this.state);
				},
			);
		});
		return promise;
	};

	private saveFastForm: SaveFastForm<ClaimData> = async (
		values,
		dirtyFields,
		reset,
		options = {
			openClaim: false,
		},
	) => {
		const {
			fields,
			claim: { locator },
		} = this.state;
		const payload = claimUpdateBuilder(
			dirtyFields,
			values,
			fields,
			FORM_NAME.claim,
			this.props.authData.auth.timezone,
		);

		if (options.openClaim) {
			payload.status = 'open';
		}

		return this.updateClaim(locator, payload);
	};

	private fetchClaim = async (claimId: string) => {
		const { authData } = this.props;
		try {
			const updatedClaim = await ClaimsApi.getClaim(
				authData.auth.authorizationToken,
				claimId,
			);
			return this.setClaim(updatedClaim.data);
		} catch (error) {
			await handleUnauthorizedResponse(error, authData);
			return null;
		}
	};

	private updateClaim = async (
		claimId: string,
		payload: ClaimUpdateRequest,
	) => {
		const { authData } = this.props;
		try {
			const updatedClaim = await ClaimsApi.updateClaim(
				authData.auth.authorizationToken,
				claimId,
				payload,
			);
			this.fetchSubclaimSummaries(updatedClaim.data);
			return this.setClaim(updatedClaim.data);
		} catch (error) {
			await handleUnauthorizedResponse(error, authData);
			return Promise.reject();
		}
	};

	private discardClaim = async (claimId: string) => {
		const { authData } = this.props;
		try {
			await ClaimsApi.discardClaim(authData.auth.authorizationToken, claimId);
			return Promise.resolve();
		} catch (error) {
			await handleUnauthorizedResponse(error, authData);
			return Promise.reject();
		}
	};

	private createSubclaimPayable = async (
		claimId: string,
		payload: SubClaimPayableCreateRequest,
	) => {
		const { authData } = this.props;
		try {
			await ClaimsApi.createSubclaimPayable(
				authData.auth.authorizationToken,
				payload,
			);
		} catch (error) {
			await handleUnauthorizedResponse(error, authData);
			return null;
		}
		return this.fetchClaim(claimId);
	};

	private reverseSubclaimPayable = async (
		claimId: string,
		payload: SubClaimPayableReverseRequest,
	) => {
		const { authData } = this.props;
		try {
			await ClaimsApi.reversePayable(authData.auth.authorizationToken, payload);
			await this.fetchClaim(claimId);
		} catch (error) {
			await handleUnauthorizedResponse(error, authData);
			return null;
		}
		return this.fetchSubclaimPayable(payload.payableLocator);
	};
}

export function withClaimContext<T>(Component: any) {
	return (props: Omit<T, 'authData' | 'claimData' | 'policyData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<PolicyContext.Consumer>
					{(policyData) => (
						<ClaimContext.Consumer>
							{(claimData) => (
								<Component
									{...props}
									claimData={claimData}
									authData={authData}
									policyData={policyData}
								/>
							)}
						</ClaimContext.Consumer>
					)}
				</PolicyContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

function withContext(Component: any) {
	return (props: Partial<Props>) => (
		<PolicyContext.Consumer>
			{(policyData) => (
				<AuthContext.Consumer>
					{(authData) => (
						<Component {...props} authData={authData} policyData={policyData} />
					)}
				</AuthContext.Consumer>
			)}
		</PolicyContext.Consumer>
	);
}

export default withContext(ClaimProvider);
